import { uploadApi } from './api';

const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB 每个分片

interface UploadProgress {
  percent: number;
  loaded: number;
  total: number;
}

/**
 * 分片上传文件
 */
export const uploadFileInChunks = async (
  file: File,
  applicationId: number,
  formData: Record<string, any>,
  onProgress?: (progress: UploadProgress) => void
): Promise<any> => {
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
  const fileId = `${file.name}-${file.size}-${file.lastModified}`;
  
  // 检查已上传的分片
  const uploadedChunks = await checkUploadedChunks(fileId, totalChunks);
  
  let uploadedSize = uploadedChunks.length * CHUNK_SIZE;
  
  // 上传每个分片
  for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
    // 跳过已上传的分片
    if (uploadedChunks.includes(chunkIndex)) {
      continue;
    }
    
    const start = chunkIndex * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, file.size);
    const chunk = file.slice(start, end);
    
    const chunkFormData = new FormData();
    chunkFormData.append('chunk', chunk);
    chunkFormData.append('chunkIndex', chunkIndex.toString());
    chunkFormData.append('totalChunks', totalChunks.toString());
    chunkFormData.append('fileId', fileId);
    chunkFormData.append('fileName', file.name);
    
    try {
      // 使用查询参数传递元数据，FormData 只传文件
      await uploadApi.post(
        `/versions/upload-chunk/${applicationId}?fileId=${encodeURIComponent(fileId)}&chunkIndex=${chunkIndex}&totalChunks=${totalChunks}&fileName=${encodeURIComponent(file.name)}`,
        chunkFormData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );
      
      uploadedSize += chunk.size;
      
      if (onProgress) {
        onProgress({
          percent: Math.round((uploadedSize / file.size) * 100),
          loaded: uploadedSize,
          total: file.size,
        });
      }
    } catch (error) {
      // 保存上传进度
      saveUploadProgress(fileId, chunkIndex);
      throw error;
    }
  }
  
  // 合并分片
  const mergeFormData = new FormData();
  mergeFormData.append('fileId', fileId);
  mergeFormData.append('fileName', file.name);
  mergeFormData.append('totalChunks', totalChunks.toString());
  
  // 添加其他表单数据
  Object.keys(formData).forEach(key => {
    mergeFormData.append(key, formData[key]);
  });
  
  const result = await uploadApi.post(`/versions/merge-chunks/${applicationId}`, mergeFormData);
  
  // 清除上传进度
  clearUploadProgress(fileId);
  
  return result;
};

/**
 * 检查已上传的分片
 */
const checkUploadedChunks = async (fileId: string, totalChunks: number): Promise<number[]> => {
  try {
    const response: any = await uploadApi.get('/versions/check-chunks', {
      params: { fileId, totalChunks }
    });
    return response.uploadedChunks || [];
  } catch (error) {
    return [];
  }
};

/**
 * 保存上传进度到本地
 */
const saveUploadProgress = (fileId: string, lastChunkIndex: number) => {
  const progress = {
    fileId,
    lastChunkIndex,
    timestamp: Date.now(),
  };
  localStorage.setItem(`upload_${fileId}`, JSON.stringify(progress));
};

/**
 * 清除上传进度
 */
const clearUploadProgress = (fileId: string) => {
  localStorage.removeItem(`upload_${fileId}`);
};

/**
 * 简单上传（不分片，适用于小文件）
 */
export const uploadFileSimple = async (
  file: File,
  applicationId: number,
  formData: FormData,
  onProgress?: (progress: UploadProgress) => void
): Promise<any> => {
  return uploadApi.post(`/versions/application/${applicationId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        onProgress({
          percent: Math.round((progressEvent.loaded / progressEvent.total) * 100),
          loaded: progressEvent.loaded,
          total: progressEvent.total,
        });
      }
    },
  });
};

/**
 * 智能上传：根据文件大小选择上传方式
 */
export const uploadFile = async (
  file: File,
  applicationId: number,
  formData: Record<string, any>,
  onProgress?: (progress: UploadProgress) => void
): Promise<any> => {
  const LARGE_FILE_SIZE = 50 * 1024 * 1024; // 50MB
  
  if (file.size > LARGE_FILE_SIZE) {
    // 大文件使用分片上传
    return uploadFileInChunks(file, applicationId, formData, onProgress);
  } else {
    // 小文件使用简单上传
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);
    Object.keys(formData).forEach(key => {
      uploadFormData.append(key, formData[key]);
    });
    return uploadFileSimple(file, applicationId, uploadFormData, onProgress);
  }
};
